//librerías
import { useCallback, useContext, useMemo, useState } from "react";
import {
  faClipboardCheck,
  faFilePdf,
  faMoneyBill,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef } from "@tanstack/react-table";
import { Button, Container, ProgressBar, Spinner } from "react-bootstrap";
import { API } from "../../../global/global";
import { AuthContext } from "../../../context/AuthContext";

//componentes
import CobroModal from "../../Formularios/CobroModal/CobroModal";
import NoCobroModal from "../../Formularios/NoCobroModal/NoCobroModal";
import CustomTable from "../../Formularios/CustomTable/CustomTable";

//types
import { ServicioPorCobrar } from "../../../types/ServicioPorCobrar";
import { useGetData } from "../../../hooks/useGetData";

//estilos
const classes = require("./ServiciosTable.module.css").default;

/**
 * ServiciosPorCobrar Component
 * @description: Componente que muestra el paginado de los servicios que no se han cobrado.
 * @date 06/03/2023.
 * @param Props Ninguno
 * @returns JSX de la tabla de servicios por cobrar.
 */

const ServiciosPorCobrar = () => {
  const [actividadActual, setActividadActual] = useState<number>(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalNoCobro, setShowModalNoCobro] = useState<boolean>(false);
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const { data: actividades, refetch } = useGetData(
    "/actividad?ultima_etapa[gte]=3&onlyCobroNull=true&includeAll=true",
    "actividadesPorCobrar"
  );

  //Función que muestra el PDF del servicio en otra pestaña.
  const mostrarPDF = useCallback(
    async (idActividad: number) => {
      setLoading(true);
      const response = await fetch(`${API}/downloadPDF/${idActividad}`, {
        method: "get",
        headers: {
          Authorization: "Bearer " + authContext.token,
        },
      });

      if (!response.ok) {
        if (response.status === 500) throw new Error("Request failed!");
        setLoading(false);
        return;
      }

      const data = await response.blob();

      //se crea el link con la descarga.
      let url = window.URL.createObjectURL(data);
      window.open(url);
      setLoading(false);
    },
    [authContext.token]
  );

  //Definicion de las columnas de la tabla
  const columns = useMemo<ColumnDef<ServicioPorCobrar>[]>(
    () => [
      {
        header: "Órden de servicio", // nombre que aparecera en el encabezado de la tabla
        accessorKey: "id", //identificador de la columna
        cell: (info) => info.getValue(), // valor que aparecera en esa columna
        footer: (props) => props.column.id,
      },
      {
        header: "Fecha y hora de la cita",
        accessorKey: "fecha",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Razón social",
        accessorFn: (row) => row.servicio.cliente.razon_social,
        id: "razon_social",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Técnico",
        accessorFn: (row) =>
          `${row.usuario.nombre} ${row.usuario.apellido_paterno} ${row.usuario.apellido_materno}`,
        id: "tecnico",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Tipo de servicio",
        accessorFn: (row) => row.tipo_servicio.nombre,
        id: "tipo_servicio",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Económico",
        accessorFn: (row) => (row.unidad.economico ? row.unidad.economico : ""),
        id: "economico",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Placas",
        accessorFn: (row) => (row.unidad.placa ? row.unidad.placa : ""),
        id: "placa",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Descripción",
        accessorFn: (row) =>
          row.descripcion_servicio ? row.descripcion_servicio : "",
        id: "descripcion",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Acciones",
        cell: (info) => (
          <div className="btn-group" role="group" aria-label="Basic example">
            {/** Boton de despliegue del PDF */}
            <Button
              title="Obtener el PDF de este servicio"
              style={{ backgroundColor: "#5DADE2", borderColor: "white" }}
              disabled={loading}
              onClick={() => mostrarPDF(info.row.original.id)}
            >
              <FontAwesomeIcon icon={faFilePdf} />
            </Button>
            {/** Boton de despliegue del modal Cobrar servicio*/}
            <Button
              title="Cobrar este servicio"
              style={{ backgroundColor: "#2874A6", borderColor: "white" }}
              onClick={() => {
                setShowModal(true);
                setActividadActual(info.row.original.id);
              }}
            >
              <FontAwesomeIcon icon={faMoneyBill} />
            </Button>
            {/** Boton de despliegue del modal No cobrar servicio */}
            <Button
              title="NO cobrar"
              style={{ backgroundColor: "#21618C", borderColor: "white" }}
              onClick={() => {
                setShowModalNoCobro(true);
                setActividadActual(info.row.original.id);
              }}
            >
              <FontAwesomeIcon icon={faClipboardCheck} />
            </Button>
          </div>
        ),
        footer: (props) => props.column.id,
      },
    ],
    [mostrarPDF, loading]
  );

  // Mientras se cargan las actividades se muestra un spinner
  if (!actividades) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner animation="grow" variant="info" />
      </div>
    );
  }

  return (
    <Container style={{ paddingBottom: "1rem" }}>
      <h1>Servicios por cobrar</h1>
      {loading && (
        <ProgressBar
          style={{ marginBottom: "1rem" }}
          label="Creando Archivo..."
          animated
          now={100}
        />
      )}
      {/** Tabla de actividades */}
      <CustomTable
        {...{
          data: actividades,
          columns,
        }}
      />
      {/** Modal de cobro */}
      {showModal && (
        <CobroModal
          actividadId={actividadActual}
          showModal={showModal}
          handleCloseModal={() => setShowModal(false)}
          refetch={refetch}
        />
      )}
      {/** Modal de no cobro */}
      {showModalNoCobro && (
        <NoCobroModal
          actividadId={actividadActual}
          showModal={showModalNoCobro}
          handleCloseModal={() => setShowModalNoCobro(false)}
          refetch={refetch}
        />
      )}
    </Container>
  );
};

export default ServiciosPorCobrar;
